import React, { useEffect } from 'react';
import PageContentWrapper from 'src/components/PageContentWrapper/PageContentWrapper';
import { CreateProjectStepsTitle } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectStepsTitle/CreateProjectStepsTitle';
import { Controller, useForm } from 'react-hook-form';
import Input from 'src/components/Input/Input';
import { Button } from 'src/components/ui/button';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { projectNameScheme } from 'src/utils/validation-schemes';
import {
  useCreateProjectMutation,
  useGetAllProjectsQuery,
  useUpdateProjectMutation,
} from 'src/services';
import { useLoader } from 'src/provider/LoaderProvider';
import { useNotification } from 'src/hooks/useNotification';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppSelector } from 'src/store';
import { ProductsType } from 'src/constants/products';
import { PATH_DASHBOARD } from 'src/constants/spa-routes';
import { CreateProjectHead } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectHead/CreateProjectHead';

const CreateProjectStep1Page = () => {
  const loader = useLoader();
  const navigate = useNavigate();
  const notification = useNotification();
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const { id, step } = useParams();
  const { projectType } = useAppSelector((s) => s.projectsSlices);
  const projectTypeVar = projectType || type;
  const isEditMode = Boolean(id);
  const { data: projects } = useGetAllProjectsQuery();
  const currentProject = projects?.find((project) => project.id === +id);

  const [
    updateProject,
    {
      data: updateProjectRes,
      isLoading: isUpdateProjectLoading,
      isSuccess: isUpdateProjectSuccess,
    },
  ] = useUpdateProjectMutation() as any;

  const [createProject, { data: createProjectRes, isLoading, isSuccess }] =
    useCreateProjectMutation() as any;

  const submit = (data) => {
    const request = {
      name: data.projectName.trim(),
    };

    if (isEditMode) {
      if (request.name === currentProject.name) {
        navigate(
          projectTypeVar === ProductsType.customer
            ? `${PATH_DASHBOARD.PROJECTS}/${id}/new/${+step + 1}?type=${projectTypeVar}`
            : `${PATH_DASHBOARD.PROJECTS}/${id}?type=${projectTypeVar}`,
          { replace: true },
        );
      }

      updateProject({
        ...request,
        projectId: id,
        cocbi_project_id: currentProject.cocbi_project_id,
        use_cocbi_model: currentProject.use_cocbi_model,
        ai_reply_generation: currentProject.ai_reply_generation,
        ai_text_lense: currentProject.ai_text_lense,
      });
    } else {
      createProject(request);
    }
  };

  useEffect(() => {
    const projectId = createProjectRes?.id || updateProjectRes?.id;

    if (projectId) {
      navigate(
        projectTypeVar === ProductsType.customer
          ? `${PATH_DASHBOARD.PROJECTS}/${projectId}/new/${+step + 1}?type=${projectTypeVar}`
          : `${PATH_DASHBOARD.PROJECTS}/${projectId}?type=${projectTypeVar}`,
        { replace: true },
      );
    }
  }, [createProjectRes, updateProjectRes]);

  useEffect(() => {
    if (isLoading || isUpdateProjectLoading) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoading, isUpdateProjectLoading]);

  useEffect(() => {
    if (isSuccess || isUpdateProjectSuccess) {
      notification.success(`${projectName} successfully ${isEditMode ? 'updated.' : 'created.'}`);
    }
  }, [isSuccess, isUpdateProjectSuccess]);

  const {
    handleSubmit,
    control,
    formState: { isValid },
    setValue,
    watch,
    clearErrors,
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      projectName: currentProject ? currentProject.name : '',
    },
    resolver: yupResolver(
      object({
        projectName: projectNameScheme({ required: true }).label('Project Name'),
      }),
    ),
  });

  const projectName = watch('projectName');

  useEffect(() => {
    if (isEditMode && currentProject) {
      setValue('projectName', currentProject.name);
    }
  }, [currentProject]);

  return (
    <React.Fragment>
      <PageContentWrapper
        menuBarVisible={false}
        className={'create-project-step items-center !gap-5'}
      >
        <CreateProjectStepsTitle step={1} title={'AI Name'} />
        <CreateProjectHead step={1} titleIndex={0} />
        <div className={'w-[455px] flex flex-col gap-5'}>
          <form id="create-project-step-form" className="w-full" onSubmit={handleSubmit(submit)}>
            <div className={'input-container w-full'}>
              <Controller
                control={control}
                name="projectName"
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    type={'text'}
                    placeholder={'Acme AI Agent'}
                    invalidMessage={error?.message}
                    value={value}
                    isValid={!error?.message}
                    onChange={onChange}
                    onFocus={() => {
                      clearErrors('projectName');
                    }}
                  />
                )}
              />
            </div>
          </form>
          <Button
            disabled={(!isEditMode && !isValid) || !projectName}
            form={'create-project-step-form'}
            type="submit"
            className="min-w-24 w-full flex flex-row h-12"
          >
            Continue
          </Button>
        </div>
      </PageContentWrapper>
    </React.Fragment>
  );
};

export default CreateProjectStep1Page;
